import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, btnPrimary, btnSecondary, card, EmptyState, InlineError, input, labelCls, Pagination, useToast } from '../ui/Kit';
import { fmtDateTime } from './common';
import { ACCEPT, kindOf, sizeLabel, uploadBatch } from './uploader';

// Candidate database → Import (bundle 4a-2): resumes (PDF / DOCX / DOC / TXT), zips of them, a CSV (name, email,
// phone, location, LinkedIn, tags). Each file goes straight to Blob with its own progress bar; then the import runs on
// the candidates worker and every file gets a result: created / merged / duplicate / failed + reason. Retry failed.

const LIMITS = { file: 10, zip: 200, csv: 5 };
const RESULT = {
  created: ['Created', 'bg-[#DCFCE7] text-[#166534]'], merged: ['Merged', 'bg-[#E8F1FB] text-[#003D82]'],
  duplicate: ['Duplicate', 'bg-[#F1F5F9] text-[#475569]'], failed: ['Failed', 'bg-[#FEE2E2] text-[#991B1B]'],
  queued: ['Queued', 'bg-[#FEF3C7] text-[#92400E]'], parsing: ['Reading…', 'bg-[#FEF3C7] text-[#92400E]'],
};
const POLL_MS = 3000;

function ResultPill({ item }) {
  const key = item.status === 'done' || item.status === 'failed' ? (item.result || item.status) : item.status;
  const [label, cls] = RESULT[key] || [key, 'bg-[#F1F5F9] text-[#475569]'];
  return <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${cls}`}>{label}</span>;
}

export function ImportStatus({ importId, onClose }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [only, setOnly] = useState('');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api(`/api/candidate-imports/${importId}?page=${page}${only ? `&only=${only}` : ''}`)
    .then((d) => { setData(d); setError(''); return d; })
    .catch((e) => { setError(e.reference ? `${e.message}` : e.message); return null; }), [importId, page, only]);
  useEffect(() => {
    let timer = null;
    let live = true;
    const tick = () => load().then((d) => { if (live && d && d.status !== 'done') timer = setTimeout(tick, POLL_MS); });
    tick();
    return () => { live = false; clearTimeout(timer); };
  }, [load]);
  const retry = async () => {
    setBusy(true);
    try { const d = await api(`/api/candidate-imports/${importId}/retry`, { method: 'POST' }); toast.show(`${d.retried} file(s) queued again`); setData(d); load(); }
    catch (e) { toast.show(e.message, 'error'); }
    setBusy(false);
  };
  if (!data) return <div className={card + ' p-5'}>{error ? <InlineError>{error}</InlineError> : <p className="text-sm text-[#64748B]">Loading…</p>}</div>;
  const c = data.counts;
  const total = c.created + c.merged + c.duplicate + c.failed + c.queued + c.parsing;
  const done = c.created + c.merged + c.duplicate + c.failed;
  const pct = total ? Math.round((done / total) * 100) : 100;
  const filters = [['', 'All'], ['created', `Created ${c.created}`], ['merged', `Merged ${c.merged}`], ['duplicate', `Duplicates ${c.duplicate}`], ['failed', `Failed ${c.failed}`]];
  return (
    <section className={card + ' p-5'} data-testid="import-status">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold">Import #{data.id} {data.status === 'done' ? '— finished' : '— running'}</h3>
          <p className="text-sm text-[#64748B]">{done} of {total} file(s) processed · started {fmtDateTime(data.created_at)}</p>
        </div>
        <div className="flex gap-2">
          {c.failed > 0 && data.items.some((i) => i.retryable) && <button className={btnSecondary} disabled={busy} onClick={retry} data-testid="retry-failed">Retry failed</button>}
          {onClose && <button className={btnSecondary} onClick={onClose}>Close</button>}
        </div>
      </div>
      <div className="h-2 bg-[#EEF1F6] rounded-full mt-3 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Import progress">
        <div className="h-full bg-[#0066CC] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex flex-wrap gap-2 mt-4" role="group" aria-label="Show">
        {filters.map(([k, label]) => (
          <button key={k || 'all'} type="button" onClick={() => { setOnly(k); setPage(1); }} aria-pressed={only === k}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${only === k ? 'border-[#0066CC] bg-[#E8F1FB] text-[#003D82]' : 'border-[#E6E9F0] bg-white text-[#334155]'}`}>{label}</button>
        ))}
      </div>
      <InlineError>{error}</InlineError>
      <ul className="divide-y divide-[#EEF1F6] mt-3" data-testid="import-items">
        {data.items.map((i) => (
          <li key={i.id} className="py-2.5 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm">
            <span className="flex-1 min-w-0 truncate font-medium" title={i.filename}>{i.filename}</span>
            <span className="sm:w-[180px] min-w-0 truncate text-[#64748B]">{i.candidate_id
              ? <button className="text-[#0066CC] font-semibold hover:underline" onClick={() => navigate(`/candidates/${i.candidate_id}`)}>{i.candidate_name}</button> : ''}</span>
            <span className="sm:w-[90px]"><ResultPill item={i} /></span>
            <span className="sm:w-[300px] text-xs text-[#64748B]">{i.reason || ''}</span>
          </li>
        ))}
        {!data.items.length && <li className="py-4 text-sm text-[#64748B]">Nothing here.</li>}
      </ul>
      <Pagination page={data.page} pageSize={data.page_size} total={data.rows_total} onPage={setPage} noun="files" />
    </section>
  );
}

export default function ImportPanel({ initialImport }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);           // [{file, kind, progress, state, error}]
  const [tags, setTags] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(initialImport || null);
  const [recent, setRecent] = useState(null);
  const loadRecent = useCallback(() => api('/api/candidate-imports').then((d) => setRecent(d.imports)).catch(() => setRecent([])), []);
  useEffect(() => { loadRecent(); }, [loadRecent, current]);

  const add = (list) => {
    const next = [];
    const bad = [];
    Array.from(list || []).forEach((f) => {
      const k = kindOf(f.name);
      if (!k) bad.push(`${f.name}: PDF, DOCX, DOC, TXT, ZIP or CSV only`);
      else if (f.size > LIMITS[k] * 1024 * 1024) bad.push(`${f.name}: up to ${LIMITS[k]} MB`);
      else next.push({ file: f, kind: k, progress: 0, state: 'ready', error: '' });
    });
    setError(bad.join(' · '));
    setFiles((cur) => [...cur, ...next].slice(0, 500));
  };
  const patch = (i, p) => setFiles((cur) => cur.map((f, j) => (j === i ? { ...f, ...p } : f)));
  const start = async () => {
    setBusy(true);
    setError('');
    try {
      const id = await uploadBatch(api, files.map((f) => f.file), {
        tags: tags.split(/[;,]/).map((t) => t.trim()).filter(Boolean), onFile: patch,
      });
      toast.show('Upload finished — reading the resumes now');
      setCurrent(id);
      setFiles([]);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };
  const totalMb = files.reduce((n, f) => n + f.file.size, 0);
  return (
    <div className="space-y-5">
      <section className={card + ' p-5'}>
        <h3 className="text-base font-bold">Import candidates</h3>
        <p className="text-sm text-[#64748B] mt-0.5">Resumes (PDF, DOCX, DOC, TXT — up to 10 MB each), a zip of up to 500 resumes (200 MB), or a CSV with
          name, email, phone, location, LinkedIn, tags. Files upload straight to secure storage; duplicates are matched automatically.</p>
        <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
          className="mt-4 border-2 border-dashed border-[#CBD5E1] rounded-xl p-6 text-center bg-[#F8FAFC]" data-testid="dropzone">
          <p className="text-sm font-semibold text-[#334155]">Drop files here</p>
          <p className="text-xs text-[#64748B] mt-1">or</p>
          <button type="button" className={btnSecondary + ' mt-2'} onClick={() => fileRef.current && fileRef.current.click()}>Choose files</button>
          <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="file-input"
            onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
        </div>
        <InlineError>{error}</InlineError>
        {files.length > 0 && (
          <>
            <ul className="mt-4 divide-y divide-[#EEF1F6]" data-testid="upload-list">
              {files.map((f, i) => (
                <li key={`${f.file.name}-${i}`} className="py-2 flex items-center gap-3 text-sm">
                  <span className="flex-1 min-w-0 truncate">{f.file.name} <span className="text-[#94A3B8]">· {sizeLabel(f.file.size)}{f.kind !== 'file' ? ` · ${f.kind.toUpperCase()}` : ''}</span></span>
                  <span className="w-28 sm:w-40 h-1.5 bg-[#EEF1F6] rounded-full overflow-hidden" role="progressbar" aria-label={`${f.file.name} upload`} aria-valuenow={f.progress} aria-valuemin={0} aria-valuemax={100}>
                    <span className={`block h-full ${f.state === 'error' ? 'bg-[#DC2626]' : 'bg-[#0066CC]'}`} style={{ width: `${f.state === 'error' ? 100 : f.progress}%` }} />
                  </span>
                  <span className="w-20 text-xs text-[#64748B] text-right" data-testid="upload-state">{f.state === 'error' ? 'Failed' : f.state === 'uploaded' ? 'Uploaded' : f.state === 'uploading' ? `${f.progress}%` : 'Ready'}</span>
                  {!busy && <button type="button" aria-label={`Remove ${f.file.name}`} className="text-[#94A3B8] hover:text-[#0F172A]" onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))}>×</button>}
                </li>
              ))}
            </ul>
            <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-3 items-end">
              <label className="block"><span className={labelCls}>Tags for everyone in this import (optional)</span>
                <input className={input} value={tags} onChange={(e) => setTags(e.target.value)} placeholder="e.g. Oct career fair; QA" /></label>
              <button className={btnPrimary} disabled={busy || !files.length} onClick={start} data-testid="start-import">
                {busy ? 'Uploading…' : `Upload ${files.length} file${files.length > 1 ? 's' : ''} (${sizeLabel(totalMb)})`}</button>
            </div>
          </>
        )}
      </section>
      {current && <ImportStatus importId={current} onClose={() => setCurrent(null)} />}
      <section className={card + ' p-5'}>
        <h3 className="text-base font-bold mb-2">Recent imports</h3>
        {recent === null ? <p className="text-sm text-[#64748B]">Loading…</p> : !recent.length ? <EmptyState title="No imports yet" hint="Your imports and their results show here." /> : (
          <ul className="divide-y divide-[#EEF1F6]">
            {recent.map((r) => (
              <li key={r.id} className="py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <button className="text-[#0066CC] font-semibold hover:underline" onClick={() => setCurrent(r.id)}>Import #{r.id}</button>
                <span className="text-[#64748B]">{fmtDateTime(r.created_at)}{r.created_by ? ` · ${r.created_by}` : ''}</span>
                <span className="text-[#334155]">{r.counts.created} created · {r.counts.merged} merged · {r.counts.duplicate} duplicates · {r.counts.failed} failed</span>
                <span className="text-xs text-[#64748B]">{r.status === 'done' ? 'Finished' : 'Running'}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
